import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { cognitoSignIn, cognitoUpdateEmail } from '../login/cognito';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID!;
const COGNITO_REGION = 'eu-central-1';

export interface EmailChangeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onEmailChangeInitiated: (accessToken: string, newEmail: string) => void;
  currentEmail: string;
}

export default function EmailChangeModal(props: EmailChangeModalProps) {
  const [step, setStep] = useState<'auth' | 'new-email'>('auth');
  const [currentEmail, setCurrentEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleReAuth = async () => {
    if (!currentEmail.trim()) {
      setError('Please enter your current email address');
      return;
    }
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsProcessing(true);
    setError('');

    const result = await cognitoSignIn(COGNITO_REGION, COGNITO_CLIENT_ID, currentEmail, password);

    if (result.success) {
      setStep('new-email');
    } else {
      setError('Invalid email or password. Please try again.');
    }
    setIsProcessing(false);
  };

  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }

    if (newEmail === currentEmail) {
      setError('New email must be different from current email');
      return;
    }

    setIsProcessing(true);
    setError('');

    // Get access token from the auth result
    const authResult = await cognitoSignIn(
      COGNITO_REGION,
      COGNITO_CLIENT_ID,
      currentEmail,
      password,
    );

    if (authResult.success && authResult.data.AccessToken) {
      const updateResult = await cognitoUpdateEmail(
        COGNITO_REGION,
        authResult.data.AccessToken,
        newEmail,
      );

      if (updateResult.success) {
        props.onEmailChangeInitiated(authResult.data.AccessToken, newEmail);
        props.setOpen(false);
      } else {
        if (updateResult.error === 'EmailAlreadyExists') {
          setError('This email is already in use. Please choose a different email.');
        } else {
          setError('Failed to update email. Please try again.');
        }
      }
    } else {
      setError('Authentication failed. Please try again.');
    }

    setIsProcessing(false);
  };

  const handleClose = () => {
    setStep('auth');
    setCurrentEmail('');
    setPassword('');
    setNewEmail('');
    setError('');
    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} className="relative z-10">
      {/* DialogBackdrop */}
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true"></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left
            shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
          >
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                  {step === 'auth' ? 'Verify Your Identity' : 'Change Email Address'}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {step === 'auth'
                      ? 'Please enter your password to verify your identity before changing your email.'
                      : 'Enter your new email address. You will receive a verification code to confirm the change.'}
                  </p>
                </div>
              </div>
            </div>

            {step === 'auth' && (
              <div className="mt-4">
                <div>
                  <label
                    htmlFor="current-email"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Current Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="current-email"
                      name="current-email"
                      type="email"
                      required
                      value={currentEmail}
                      onChange={(e) => setCurrentEmail(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter your current email"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    onClick={handleReAuth}
                    disabled={isProcessing}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm
                    font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline
                    focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600
                    disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Verifying...' : 'Verify Identity'}
                  </button>
                </div>
              </div>
            )}

            {step === 'new-email' && (
              <div className="mt-4">
                <div>
                  <label
                    htmlFor="current-email"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Current Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="current-email"
                      name="current-email"
                      type="email"
                      value={props.currentEmail}
                      disabled
                      className="block w-full rounded-md bg-gray-100 px-3 py-1.5 text-base text-gray-500
                      outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="new-email" className="block text-sm font-medium text-gray-900">
                    New Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="new-email"
                      name="new-email"
                      type="email"
                      required
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter new email address"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                <div className="mt-5 sm:mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep('auth')}
                    disabled={isProcessing}
                    className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-900
                    hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailChange}
                    disabled={isProcessing}
                    className="flex-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white
                    shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2
                    focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-300
                    disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Updating...' : 'Update Email'}
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
